import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";

import { debounce } from "@mui/material";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";

import { Icon, Modal } from "@/components";

import { classNames } from "@/utils/classNames";
import { toPriorityFilterItems } from "@/utils/priorityFilterOptions";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useSearch } from "@/utils";
import {
	clearInbox,
	listSelector,
	setCurrentLeadIndex,
} from "@/pages/Private/redux/list/list.slice";
import {
	AssignStatus,
	AssignStatusFilter,
	InclusionFilterStatus,
	ListSourceType,
	PriorityFilterStatus,
} from "@/enum/list.enum";
import {
	useGetLeanListsQuery,
	useLazyGetAllProspectsQuery,
	useUpdateListPersonSendersMutation,
} from "@/pages/Private/redux/list/list.api";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGetCompanyLifecycleQuery } from "@/pages/Private/redux/company/company.api";

import { useOutreachActivation } from "@/components/OutreachActivation/useOutreachActivation";

import {
	DropdownWithCheckboxes,
	DropdownWithCheckboxesItem,
} from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { Order } from "@/types/types";

import { NoSenderModal } from "@/components/NoSenderModal/NoSenderModal";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";

import { MessagesFilterBar } from "../../components/MessagesFilterBar/MessagesFilterBar";
import { MessagesLoadError } from "../../components/MessagesLoadError/MessagesLoadError";
import { ReadOnlyPlanBanner } from "../../components/ReadOnlyPlanBanner";
import { resolveMessagesAccessMode } from "../../utils/messagesAccessMode";
import { ComposeLeadsPagingSpinner } from "./ComposeLeadsPagingSpinner";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { FilterModal, IFilterWithValues } from "../../../List/components/FilterModal";
import { List, ListPerson } from "../../../List/schema/list";
import { isDummyProspect } from "../../../List/utils/columns/dummyProspect";
import { Lead } from "../../../List/components/CampaignOverview/components/Lead/Lead";
import { useBulkAssign } from "../../../List/utils/useBulkAssign";
import { AddToTrackerFooterButton } from "../../components/AddToTrackerFooterButton/AddToTrackerFooterButton";
import { LeadDetailRail } from "../../components/LeadDetailRail";
import { Sender } from "../../../List/components/CampaignOverview/components/Sender";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

import { useAddInboxChat } from "../../../List/components/CampaignOverview/utils/useAddInboxChat";
import { useFeedDraftGeneration } from "../../../List/components/CampaignOverview/utils/useFeedDraftGeneration";
import { CombinedSignalFeed } from "../../../ProfileTracker/components/CustomColumns/SignalTimeline";
import { prospectHasFeedSignal } from "../../../ProfileTracker/components/CustomColumns/SignalTimeline/combinedSignalFeedUtils";
import {
	buildDefaultCampaignSenders,
	campaignSendersFor,
	mergeCurrentCampaignSenders,
	toSenderIdentities,
} from "../../utils/campaignSenders";
import { CopywritingCampaign } from "../../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { useSenderConfirmGate } from "../../../List/components/CampaignOverview/utils/useSenderConfirmGate";
import { resolveComposeSendItems } from "../../utils/resolveComposeSendItems";
import { useComposeAddPerson } from "./useComposeAddPerson";
import { useComposerFilter } from "./useComposerFilter";
import { useComposeUrlSync } from "./useComposeUrlSync";
import { ComposeHeader } from "./components/ComposeHeader";
import { ConfirmScheduleSequencesModal } from "./components/ConfirmScheduleSequencesModal";

enum Tabs {
	CHAT = "CHAT",
	FEED = "FEED",
}

export const Compose: FunctionComponent = () => {
	const { t } = useTranslation();

	const { type = "ready-to-send" } = useParams();

	const { data: sendingUsers } = useGetSendersUsersQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});
	const { data: lifecycle } = useGetCompanyLifecycleQuery();
	const accessMode = resolveMessagesAccessMode({
		lifecycleState: lifecycle?.state,
		hasSendingUsers: (sendingUsers?.data?.length ?? 0) > 0,
		sendingUsersLoaded: Boolean(sendingUsers?.data),
	});

	const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.CHAT);

	const handleTabsChange = useCallback((value: Tabs): void => {
		setCurrentTab(value);

		if (value === Tabs.CHAT) {
			setTimeout(() => {
				scrollToBottom();
			}, 100);
		}
	}, []);

	const assignStatusFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return params.get("assignStatus")
			? (JSON.parse(params.get("assignStatus") || "[]") as AssignStatusFilter[])
			: [AssignStatusFilter.DRAFT];
	};

	const [selectedAssignStatus, setSelectedAssignStatus] =
		useState<AssignStatusFilter[]>(assignStatusFromUrl());

	const inboxChat = useAddInboxChat({ prefillDrafts: true });
	const { renderChatlog, renderSendButtons, scrollToBottom } = inboxChat;
	const { openGeneratePanel, renderGeneratePanel } = inboxChat;

	const { renderFeedDraftSnackbar } = useFeedDraftGeneration();

	const [configureSenders, setConfigureSenders] = useState<boolean>(false);

	const { sortingInbox, inbox, currentLeadIndex, meta, inboxLoadFailed } =
		useAppSelector(listSelector);

	useEffect(() => {
		const prospect = inbox[currentLeadIndex];

		if (!prospect) {
			return;
		}

		// Don't auto-switch away from the Feed tab while the user browses a prospect's signals there.
		if (currentTab === Tabs.FEED && prospectHasFeedSignal(prospect)) {
			return;
		}

		// Compose is chat-only — always land the selected lead on the Chat tab and scroll to bottom.
		setCurrentTab(Tabs.CHAT);

		setTimeout(() => {
			scrollToBottom();
		}, 1);
	}, [inbox, currentLeadIndex]);

	const defaultSenders = useMemo<Sender[]>(
		() => buildDefaultCampaignSenders(sendingUsers?.data),
		[sendingUsers]
	);

	const currentSenders = useMemo<Sender[]>(
		() => mergeCurrentCampaignSenders(defaultSenders, inbox[currentLeadIndex]),
		[inbox, currentLeadIndex, defaultSenders]
	);

	const [showComposeModal, setShowComposeModal] = useState<boolean>(true);
	const [showConfirmScheduleSequencesModal, setShowConfirmScheduleSequencesModal] =
		useState<boolean>(false);

	const [selected, setSelected] = useState<number[]>([]);

	const {
		handleBulkReassignInternalCampaigns,
		bulkReassignIsLoading,
		renderAddToTrackerModal,
		setShowAddToTrackerModal,
		bulkAddToTrackerIsLoading,
	} = useBulkAssign();
	const { assignWithActivation, activationModal } = useOutreachActivation();
	// A campaign send that goes out as colleagues asks first, once, listing each (PREC-4312, Q5).
	const { confirmSenders, senderConfirmModal } = useSenderConfirmGate();

	const getPriorityFilterStatusFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return params.get("priorityFilterStatus")
			? (JSON.parse(params.get("priorityFilterStatus") || "[]") as PriorityFilterStatus[])
			: [PriorityFilterStatus.ALL_WITH_PRIORITY];
	};

	const sortState = {
		sortBy: "createdAt",
		sortOrder: Order.DESC,
	};
	const [listData, setListData] = useState<List[]>([]);

	const [selectedLists, setSelectedLists] = useState<CustomListsAutocompleteForSequencerItem[]>([]);
	const [listLoading, setListLoading] = useState(false);

	const [listPage, setListPage] = useState<number>(1);

	const {
		setSearchValue: setListSearchValue,
		query: listQuery,
		altered: listAltered,
		setAltered,
	} = useSearch();

	const { data: lists, isFetching: listIsFetching } = useGetLeanListsQuery({
		page: listPage,
		limit: 15,
		searchValue: listQuery,
		sorting: sortState,
	});

	useEffect(() => {
		if (!listIsFetching) {
			setAltered(false);
		}
	}, [listIsFetching]);

	useEffect(() => {
		if (lists) {
			if (listAltered) {
				setListData(lists.data);
			} else {
				const newInListData = lists.data.filter((list) => !listData.some((l) => l.id === list.id));

				setListData((prevData) => [...prevData, ...newInListData]);
			}

			setListLoading(false);
		}
	}, [lists, listAltered]);

	const handleListLoadMore = () => {
		if (!listIsFetching && listPage < (lists?.meta?.pageCount || 0)) {
			setTimeout(() => {
				setListPage(listPage + 1);
			}, 500);
		}
	};

	const [priorityFilterStatus, setPriorityFilterStatus] = useState<PriorityFilterStatus[]>(
		getPriorityFilterStatusFromUrl()
	);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const leadsEndRef = useRef<HTMLDivElement>(null);
	const [updateListPersonSenders] = useUpdateListPersonSendersMutation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [
		getUnassigned,
		{ isFetching: isFetchingGetUnassigned, isLoading: isLoadingGetUnassigned },
	] = useLazyGetAllProspectsQuery();

	// GlobalSearchModal "Write message" integration — opens the chat, relaxes the Score filter, and
	// selects the freshly-added lead by id once the refetch settles (see useComposeAddPerson).
	const handleComposePersonAdded = useComposeAddPerson({
		inbox,
		isFetchingInbox: isFetchingGetUnassigned,
		onOpenChat: () => setCurrentTab(Tabs.CHAT),
		onShowAllPriority: () => setPriorityFilterStatus([PriorityFilterStatus.ALL]),
	});

	const { limit, query, setSearchValue, searchValue } = useSearch();

	const [page, setPage] = useState<number>(1);
	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

	const [isShown, setIsShown] = useState<boolean>(false);

	const loadMoreData = useCallback(() => {
		if (!isFetchingGetUnassigned && page < (meta?.pageCount || 0) && page === meta.page) {
			setPage(page + 1);
		}
	}, [page, isFetchingGetUnassigned, meta]);

	useEffect(() => {
		if (inbox.length === 0) {
			return;
		}

		const tableContainer = document.querySelector("#leads-container");

		const isLastElementVisible = () => {
			const rect = leadsEndRef.current?.getBoundingClientRect();

			if (rect) {
				const isVisible =
					rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

				if (isVisible) {
					if (!isShown) {
						setIsShown(true);
						loadMoreData();
					}
				} else {
					setIsShown(false);
				}
			}
		};

		const handleScroll = debounce(isLastElementVisible, 500);

		if (tableContainer) {
			tableContainer?.addEventListener("scroll", handleScroll);

			isLastElementVisible();
		}

		return () => {
			tableContainer?.removeEventListener("scroll", handleScroll);
		};
	}, [inbox, leadsEndRef, page, isShown]);

	const ts = (key: string) => t(`messages.${key}`);

	const tabs = [
		{
			label: ts("tabs.chat"),
			value: Tabs.CHAT,
		},
		...(inbox.some(
			(lp) =>
				(lp.prospect?.organization?.trackerSignalResults?.length ?? 0) > 0 ||
				(lp.prospect?.trackerSignalResults?.length ?? 0) > 0
		)
			? [{ label: "Feed", value: Tabs.FEED }]
			: []),
	];

	const getFilterFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(params.get("filter") || "{}");
	};

	const [filter, setFilter] = useState<IFilterWithValues | undefined>(getFilterFromUrl());
	const [showLeads, setShowLeads] = useState<boolean>(true);
	const [showSingleLead, setShowSingleLead] = useState<boolean>(false);
	const handleReachOutFromSignal = useCallback(() => {
		setCurrentTab(Tabs.CHAT);
		setShowSingleLead(true);
		setTimeout(() => scrollToBottom(), 100);
	}, [scrollToBottom]);

	const handleGenerateDraftsFromSignal = useCallback(
		(_inboxIndex: number, signal: { summary?: string | null; title?: string | null }) => {
			// CombinedSignalFeed already selected the lead; seed + open the panel.
			openGeneratePanel(signal.summary || signal.title || null);
		},
		[openGeneratePanel]
	);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	// Mirrors assign-status/priority/filter selections into the URL and seeds the search box
	// from it on mount (see useComposeUrlSync.ts).
	useComposeUrlSync({
		selectedAssignStatus,
		priorityFilterStatus,
		filter,
		setSearchValue,
		navigate,
	});

	function handleSetCurrentLead(index: number) {
		dispatch(setCurrentLeadIndex(index));
	}

	const { composerOptions, selectedComposers, composerIds, handleComposerSelect } =
		useComposerFilter();

	// Clear inbox and reset to page 1 when filters/search/sorting change (but not when only page changes)
	useEffect(() => {
		dispatch(clearInbox());
		dispatch(setCurrentLeadIndex(0));
		setPage(1);
	}, [dispatch, filter, type, query, sortingInbox, priorityFilterStatus, composerIds]);

	// Fetch data when any parameter changes (including page)
	useEffect(() => {
		const assignStatus = selectedAssignStatus.reduce<AssignStatus[]>((acc, status) => {
			if (status === AssignStatusFilter.ALL) {
				return [...acc, AssignStatus.DRAFT, AssignStatus.SAVED_FOR_LATER, AssignStatus.ARCHIVED];
			}

			return [...acc, status as unknown as AssignStatus];
		}, []);

		// No manual abort here: a stale response (e.g. the previous tab's request landing after a
		// client-side tab switch) is ignored by the slice's request-id guard (see list-inbox.helper).
		getUnassigned({
			page,
			limit,
			searchValue: query,
			sorting: sortingInbox,
			assignStatus: assignStatus ?? undefined,
			filter,
			inclusionFilterStatus: [InclusionFilterStatus.ADDED],
			priorityFilterStatus: priorityFilterStatus,
			specialType: "COMPOSE_LIST",
			composers: composerIds,
		});
	}, [
		getUnassigned,
		type,
		limit,
		page,
		query,
		sortingInbox,
		filter,
		priorityFilterStatus,
		selectedLists,
		selectedAssignStatus,
		composerIds,
	]);

	const [senders, setSenders] = useState<Sender[]>(currentSenders);

	useEffect(() => {
		setSenders(currentSenders);
	}, [currentSenders]);

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			if (!isSequencerActive) {
				navigate(`/app/lists/`);
			}
		}, 500);
	}, [isSequencerActive, navigate]);

	const currentListPerson: ListPerson | undefined = useMemo(() => {
		if (currentLeadIndex === undefined || currentLeadIndex < 0) {
			return undefined;
		}

		return inbox[currentLeadIndex];
	}, [inbox, currentLeadIndex]);

	// "Send Selected" asks to start any target campaign that is not sending (PREC-3296); a cancel
	// keeps the confirm modal open with nothing written.
	const handleConfirmScheduleSequences = useCallback(async () => {
		const items = resolveComposeSendItems(selected, inbox);
		const sendsAs = items.flatMap((item) =>
			campaignSendersFor({
				override: configureSenders ? senders : undefined,
				prospect: item.prospect,
				campaign: (
					item.prospect.list?.campaignConfig as Record<string, CopywritingCampaign> | undefined
				)?.[item.campaignId],
			})
		);

		if (!(await confirmSenders(toSenderIdentities(sendsAs), 2))) {
			return;
		}

		const assigned = await assignWithActivation({
			listId: currentListPerson?.list.id || 0,
			campaignIds: items.map((item) => item.campaignId),
			assign: async () => {
				if (configureSenders) {
					await Promise.all(
						items.map((item) =>
							updateListPersonSenders({ id: item.prospect.id, senders: senders }).unwrap()
						)
					);
				}

				await handleBulkReassignInternalCampaigns({
					id: currentListPerson?.list.id || 0,
					selectedItems: items.map((item) => ({
						listPersonId: item.prospect.id,
						campaignId: item.campaignId,
					})),
					filter: filter || {},
					inclusionStatus: InclusionFilterStatus.ADDED,
				});
			},
		});

		if (!assigned) {
			return;
		}

		setSelected([]);
		setConfigureSenders(false);
		setShowConfirmScheduleSequencesModal(false);
	}, [
		assignWithActivation,
		selected,
		inbox,
		currentListPerson,
		configureSenders,
		senders,
		filter,
		handleBulkReassignInternalCampaigns,
		updateListPersonSenders,
		confirmSenders,
	]);

	return (
		<>
			{accessMode === "no-senders" && <NoSenderModal />}
			<div
				className={classNames(
					"sm:block flex flex-col items-start justify-between w-full  h-full overflow-hidden",
					accessMode === "no-senders" ? "blur-sm" : ""
				)}
			>
				<div className="bg-brand-white  flex flex-col justify-between items-start w-full h-[calc(100vh-48px)]">
					{accessMode === "read-only" && (
						<ReadOnlyPlanBanner readOnlyUntil={lifecycle?.readOnlyUntil ?? null} />
					)}
					<ComposeHeader onCompose={() => setShowComposeModal(true)} />
					<div className="flex bg-white border-b border-border-light w-full flex-shrink-0">
						{tabs.map((tab, index) => (
							<button
								key={index}
								className={classNames(
									"px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer",
									currentTab === tab.value
										? "border-brand-light text-brand-light"
										: "border-transparent text-text-meta hover:text-text hover:border-gray-300"
								)}
								type="button"
								onClick={() => handleTabsChange(tab.value)}
							>
								{tab.label}
							</button>
						))}
					</div>
					<MessagesFilterBar
						className="border-b border-border-light"
						filterCount={filter ? Object.keys(filter).length : 0}
						onOpenFilter={() => setShowFilterModal(true)}
						searchValue={searchValue}
						onSearchChange={(value) => {
							setSearchValue(value);
							setPage(1);
						}}
						dropdowns={
							<>
								<div className="w-fit min-w-[120px] max-w-[220px]">
									<DropdownWithCheckboxes
										data={composerOptions}
										defaultValue={selectedComposers}
										floating={true}
										handleSelect={(values: DropdownWithCheckboxesItem[]) => {
											setPage(1);

											handleComposerSelect(values);
										}}
										size={ButtonSize.XS}
										title="Composer"
										value={selectedComposers}
									/>
								</div>
								<div className="w-fit min-w-[120px] max-w-[220px]">
									<DropdownWithCheckboxes
										data={toPriorityFilterItems((status) =>
											t(`inbox.priorityFilterOptions.${status}`)
										)}
										defaultValue={
											priorityFilterStatus && priorityFilterStatus.length > 0
												? priorityFilterStatus.map((i) => {
														return {
															title: t(`inbox.priorityFilterOptions.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL}`),
															id: PriorityFilterStatus.ALL,
														},
													]
										}
										floating={true}
										handleSelect={(values: DropdownWithCheckboxesItem[]) => {
											setPage(1);

											const addedValue = values.find(
												(val) => !priorityFilterStatus.includes(val.id as PriorityFilterStatus)
											);

											const newValues = values.filter(
												(val) =>
													val.id !== PriorityFilterStatus.ALL &&
													val.id !== PriorityFilterStatus.ALL_WITH_PRIORITY
											);

											if (addedValue && addedValue.id === PriorityFilterStatus.ALL) {
												setPriorityFilterStatus([PriorityFilterStatus.ALL]);
											} else if (
												addedValue &&
												addedValue.id === PriorityFilterStatus.ALL_WITH_PRIORITY
											) {
												setPriorityFilterStatus([PriorityFilterStatus.ALL_WITH_PRIORITY]);
											} else {
												setPriorityFilterStatus(newValues.map((v) => v.id as PriorityFilterStatus));
											}
										}}
										size={ButtonSize.XS}
										title="Score"
										value={
											priorityFilterStatus && priorityFilterStatus.length > 0
												? priorityFilterStatus.map((i) => {
														return {
															title: t(`inbox.priorityFilterOptions.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(
																`inbox.priorityFilterOptions.${PriorityFilterStatus.ALL_WITH_PRIORITY}`
															),
															id: PriorityFilterStatus.ALL_WITH_PRIORITY,
														},
													]
										}
									/>
								</div>

								<div className="w-fit min-w-[120px] max-w-[220px]">
									<DropdownWithCheckboxes
										data={[
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.ALL}`),
												id: AssignStatusFilter.ALL,
											},
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.DRAFT}`),
												id: AssignStatusFilter.DRAFT,
											},
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.SAVED_FOR_LATER}`),
												id: AssignStatusFilter.SAVED_FOR_LATER,
											},
											{
												title: t(`inbox.assignStatus.${AssignStatusFilter.ARCHIVED}`),
												id: AssignStatusFilter.ARCHIVED,
											},
										]}
										defaultValue={
											selectedAssignStatus && selectedAssignStatus.length > 0
												? selectedAssignStatus.map((i) => {
														return {
															title: t(`inbox.assignStatus.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.assignStatus.${AssignStatusFilter.DRAFT}`),
															id: AssignStatusFilter.DRAFT,
														},
													]
										}
										floating={true}
										handleSelect={(values: DropdownWithCheckboxesItem[]) => {
											setPage(1);

											const addedValue = values.find(
												(val) => !selectedAssignStatus.includes(val.id as AssignStatusFilter)
											);

											if (addedValue && addedValue.id === AssignStatusFilter.ALL) {
												setSelectedAssignStatus([AssignStatusFilter.ALL]);
											} else {
												setSelectedAssignStatus(
													values
														.filter((value) => value.id !== AssignStatusFilter.ALL)
														.map((value) => value.id as AssignStatusFilter)
												);
											}
										}}
										size={ButtonSize.XS}
										title="Status"
										value={
											selectedAssignStatus && selectedAssignStatus.length > 0
												? selectedAssignStatus.map((i) => {
														return {
															title: t(`inbox.assignStatus.${i}`),
															id: i,
														};
													})
												: [
														{
															title: t(`inbox.assignStatus.${AssignStatus.DRAFT}`),
															id: AssignStatus.DRAFT,
														},
													]
										}
									/>
								</div>
							</>
						}
					/>
					<div
						className="flex w-full h-full flex-row relative overflow-hidden"
						// overflow-hidden boxes can still be scrolled programmatically
						// (scrollIntoView, focus scrolling) and the user can never scroll
						// them back — pin the position so the top controls don't get stuck
						// clipped under the tab bar.
						onScroll={(e) => {
							e.currentTarget.scrollTop = 0;
							e.currentTarget.scrollLeft = 0;
						}}
					>
						<div
							className={`bg-white w-[467px] min-w-[467px] transition-all z-15 duration-300 h-full border-r pl-0 border-t border-border-light pb-0 mb-4 relative ${
								showLeads ? "translate-x-0" : "translate-x-[0] !w-[35px] !min-w-[35px]"
							}`}
						>
							<CollapseGrabber
								ariaLabel={showLeads ? t("basics.collapse") : t("basics.expand")}
								collapsed={!showLeads}
								edge="right"
								onToggle={() => setShowLeads(!showLeads)}
							/>
							<div className={classNames("overflow-x-hidden h-full flex flex-col")}>
								<div
									className="flex flex-col max-h-full overflow-y-auto border-t border-border-light scrollbar-hide w-full"
									id="leads-container"
								>
									{inbox.map((row: ListPerson) => (
										<Lead
											key={`${row.id}-${row.prospect?.id}`}
											pKey={`${row.id}-${row.prospect?.id}`}
											isCollapsed={!showLeads}
											handleSetCurrentLead={handleSetCurrentLead}
											listId={row.list.id}
											row={row}
											selected={selected}
											setSelected={setSelected}
										/>
									))}
									{showLeads && <MessagesLoadError />}
									<ComposeLeadsPagingSpinner
										isFetching={isFetchingGetUnassigned}
										hasMorePages={Boolean(!inboxLoadFailed && meta && page < meta.pageCount)}
										leadsEndRef={leadsEndRef}
									/>
								</div>
							</div>
						</div>

						<div className={classNames("h-full min-h-[100%] w-full z-5 relative")}>
							{selected.length > 0 && (
								<div className="absolute top-0 left-0 h-full w-full text-center bg-white/50 z-50">
									<div className="text-center absolute top-1/2 text-text-disabled left-1/2 -translate-x-1/2 -translate-y-1/2">
										you can not edit the sequence when you have selected leads. <br />
										<span className="font-bold">Unsaved changes will be lost.</span>
									</div>
								</div>
							)}
							<div
								className={classNames(
									"h-full min-h-[100%] w-full z-5",
									selected.length > 0 ? "blur" : ""
								)}
							>
								{currentTab === Tabs.CHAT && (
									<div
										className={classNames(
											"h-full w-full z-5 relative flex flex-col overflow-hidden",
											selected.length > 0 ? "blur" : ""
										)}
									>
										<div className="flex-1 min-h-0 overflow-hidden">
											{inbox?.length > 0 && renderChatlog(selected, isLoadingGetUnassigned)}
										</div>
									</div>
								)}
								{currentTab === Tabs.FEED && (
									<div className="w-full h-full pr-12">
										{/* Opens on Feed without a tab change; chat tab renders it via renderChatlog. */}
										{renderGeneratePanel()}
										<CombinedSignalFeed
											onGenerateDraftMessages={handleGenerateDraftsFromSignal}
											onReachOutToProspect={handleReachOutFromSignal}
										/>
									</div>
								)}
							</div>
						</div>
						{type !== "inbox" && (
							<LeadDetailRail
								listPerson={currentListPerson}
								setShow={setShowSingleLead}
								show={showSingleLead}
							/>
						)}
					</div>

					{showFilterModal && (
						<FilterModal
							filter={filter}
							listType={ListSourceType.CSV_ENRICHMENTS}
							setFilter={(e) => {
								setPage(1);
								setFilter(e);
							}}
							setShowFilterModal={setShowFilterModal}
							showFilterModal={showFilterModal}
						/>
					)}
					{showComposeModal && (
						<GlobalSearchModal
							isOpened={showComposeModal}
							setIsOpened={setShowComposeModal}
							singleSelect
							onPersonAdded={handleComposePersonAdded}
						/>
					)}
				</div>

				<div className="absolute mt-14 h-12 flex flex-row justify-between items-center w-auto border-t border-t-border px-3 py-2 rounded-t-16 bottom-0 right-0 z-[1000] bg-white w-full">
					<div></div>
					<div className="flex flex-row self-left items-center gap-x-2">
						<AddToTrackerFooterButton
							isLoading={bulkAddToTrackerIsLoading}
							listPersonId={currentListPerson?.id}
							organizationId={currentListPerson?.prospect?.organization?.id}
							onAdd={() => setShowAddToTrackerModal(true)}
						/>
						{selected.length > 0 && accessMode !== "read-only" ? (
							<Button
								color={ButtonColor.BRAND}
								image={<Icon className="mr-2" icon={faPaperPlane} />}
								isLoading={bulkReassignIsLoading}
								size={ButtonSize.XS}
								title={"Send Selected"}
								onClick={() => {
									setShowConfirmScheduleSequencesModal(true);
								}}
							/>
						) : (
							<></>
						)}
						{currentListPerson &&
							!isDummyProspect(currentListPerson) &&
							currentTab === Tabs.CHAT &&
							accessMode !== "read-only" &&
							renderSendButtons()}
					</div>
				</div>

				{showConfirmScheduleSequencesModal && (
					<ConfirmScheduleSequencesModal
						configureSenders={configureSenders}
						count={selected.length}
						senders={senders}
						sendingUsers={sendingUsers?.data ?? []}
						setSenders={setSenders}
						onClose={() => setShowConfirmScheduleSequencesModal(false)}
						onConfirm={handleConfirmScheduleSequences}
						onToggleConfigureSenders={() => setConfigureSenders(!configureSenders)}
					/>
				)}
				{currentListPerson &&
					renderAddToTrackerModal({ selectedItems: [{ listPersonId: currentListPerson.id }] })}
				{renderFeedDraftSnackbar()}
				{activationModal}
				{senderConfirmModal}
			</div>
		</>
	);
};
